/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    FiArrowRight,
    FiArrowLeft,
    FiBox,
    FiTag,
    FiImage,
    FiLayout,
    FiEdit2,
    FiShoppingCart,
    FiInfo,
    FiCheckCircle,
    FiAlertTriangle
} from "react-icons/fi";
import PageContainer from "../components/PageContainer";
import { useUI } from "../theme/ThemeProvider";
import clsx from "clsx";

export default function ProductDetails() {
    const { itemId } = useParams();
    const navigate = useNavigate();
    const { theme, lang, colors } = useUI();

    const isDark = theme === "dark";
    const isArabic = lang === "ar";

    const [product, setProduct] = useState(null);
    const [stockInfo, setStockInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImage, setActiveImage] = useState(null);

    const baseURL = (
        process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api"
    ).replace(/\/+$/, "");
    const baseImageUrl = baseURL.replace("/api", "");

    const token = localStorage.getItem("authToken");
    const tenantId = localStorage.getItem("tenantId");

    useEffect(() => {
        if (!itemId) return;

        const fetchData = async () => {
            try {
                setLoading(true);

                // 1. Fetch Item Details
                const itemRes = await fetch(
                    `${baseURL}/GetItems?tenantId=${tenantId}&itemId=${itemId}`,
                    {
                        headers: {
                            Accept: "application/json",
                            Authorization: token ? `Bearer ${token}` : "",
                        },
                    }
                );

                // 2. Fetch Stock Details
                const stockRes = await fetch(
                    `${baseURL}/ItemsStock?itemId=${itemId}&tenantId=${tenantId}`,
                    {
                        headers: {
                            Accept: "application/json",
                            Authorization: token ? `Bearer ${token}` : "",
                        },
                    }
                );

                if (itemRes.ok) {
                    const itemData = await itemRes.json();
                    if (Array.isArray(itemData) && itemData.length > 0) {
                        const prod = itemData[0];
                        setProduct(prod);
                        // Get main product image
                        const mainProductImg = prod.mainImageUrl || prod.imageUrl || null;

                        // Set first variant's image as active by default, fallback to main product image
                        const firstImg = prod.variants?.find(v => v.imageUrl)?.imageUrl || mainProductImg;
                        setActiveImage(firstImg || null);
                    }
                }

                if (stockRes.ok) {
                    const stockData = await stockRes.json();
                    if (Array.isArray(stockData) && stockData.length > 0) {
                        setStockInfo(stockData[0]);
                    }
                }

            } catch (err) {
                console.error("ProductDetails error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [itemId, baseURL, token, tenantId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
            </div>
        );
    }

    if (!product) {
        return (
            <PageContainer>
                <div className="text-center py-20">
                    <p className="text-red-500 font-bold mb-4">{isArabic ? "المنتج غير موجود" : "Product not found"}</p>
                    <button onClick={() => navigate(-1)} className="text-purple-600 active:underline">
                        {isArabic ? "رجوع" : "Go Back"}
                    </button>
                </div>
            </PageContainer>
        );
    }

    const Card = ({ children, className, title, icon: Icon }) => (
        <div className={clsx(
            "rounded-3xl border transition-all duration-300 shadow-sm overflow-hidden",
            isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-100",
            className
        )}>
            {title && (
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-3">
                    {Icon && <div className="p-2 rounded-xl bg-purple-600/10 text-purple-600"><Icon size={18} /></div>}
                    <h3 className="font-bold text-base">{title}</h3>
                </div>
            )}
            <div className="p-6">
                {children}
            </div>
        </div>
    );

    const StatCard = ({ icon: Icon, label, value, color }) => (
        <div className={clsx(
            "rounded-2xl border p-4 flex flex-col gap-2 transition-all active:shadow-md",
            isDark ? "bg-[#0f172a] border-[#334155]" : "bg-white border-gray-50"
        )}>
            <div className="flex items-center gap-2 text-gray-400">
                <Icon size={16} />
                <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
            </div>
            <div className={clsx("text-xl font-black", color || (isDark ? "text-white" : "text-gray-900"))}>
                {value}
            </div>
        </div>
    );

    const buildImgUrl = (path) => {
        if (!path) return "https://placehold.co/400x400/e0e0e0/666?text=No+Image";
        if (path.startsWith("http")) return path;
        const cleanBase = baseImageUrl;
        const parts = path.replace(/^\/+/, "").split("/");
        if (parts.length >= 3 && (parts[0] === "TenantDtl" || parts[0] === "Logo")) {
            return `${cleanBase}/uploads/${parts[1]}/${parts[0]}/${parts[2]}`;
        }
        return `${cleanBase}${path.startsWith("/") ? "" : "/"}${path}`;
    };

    const getStatusInfo = (status) => {
        switch (status) {
            case "OUT_OF_STOCK":
                return { label: isArabic ? "نفذ من المخزن" : "Out of Stock", color: "text-red-500", bg: "bg-red-500/10", icon: FiAlertTriangle };
            case "LOW_STOCK":
                return { label: isArabic ? "كمية منخفضة" : "Low Stock", color: "text-amber-500", bg: "bg-amber-500/10", icon: FiAlertTriangle };
            default:
                return { label: isArabic ? "متوفر" : "Available", color: "text-emerald-500", bg: "bg-emerald-500/10", icon: FiCheckCircle };
        }
    };

    const stockStatus = getStatusInfo(stockInfo?.stockStatus || (product.variants?.some(v => (v.sizes?.reduce((s, sz) => s + (sz.stockQty || 0), 0) || v.stockQty || 0) > 0) ? "AVAILABLE" : "OUT_OF_STOCK"));

    // Get manual total stock from variants (stock.quantity)
    const totalQtyFromVariants = product.variants?.reduce((sum, v) => {
        const q = Number(v.stock?.quantity ?? v.stock?.Quantity);
        if (Number.isFinite(q)) return sum + q;
        if (v.sizes && Array.isArray(v.sizes)) {
            return sum + v.sizes.reduce((sSum, sz) => sSum + (sz.stockQty || 0), 0);
        }
        return sum + (v.stockQty || v.quantity || 0);
    }, 0) || 0;

    // Get principal product images for fallback
    const mainProductImg = product.mainImageUrl || product.imageUrl || null;

    // Get gallery images from variants, falling back to main product image for each variant if its image is missing
    const gallery = [...new Set(
        product.variants && Array.isArray(product.variants) && product.variants.length > 0
            ? product.variants.map(v => v.imageUrl || v.mainImageUrl || mainProductImg).filter(Boolean)
            : [mainProductImg].filter(Boolean)
    )];

    return (
        <PageContainer title="" dir={isArabic ? "rtl" : "ltr"}>
            <div className="max-w-6xl mx-auto space-y-6 pb-20">

                {/* Header Actions */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className={clsx(
                                "flex items-center justify-center w-10 h-10 rounded-full border transition-all active:scale-95",
                                isDark ? "border-gray-700 active:bg-gray-800 text-gray-300" : "border-gray-200 active:bg-gray-50 text-gray-600"
                            )}
                        >
                            {isArabic ? <FiArrowRight size={20} /> : <FiArrowLeft size={20} />}
                        </button>
                        <div>
                            <h1 className={clsx("text-xl md:text-2xl font-black", isDark ? "text-white" : "text-gray-900")}>
                                {isArabic ? "تفاصيل المنتج" : "Product Details"}
                            </h1>
                            <span className="text-xs font-bold text-gray-400">SKU: {product.itemCode}</span>
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                        icon={FiBox}
                        label={isArabic ? "إجمالي المخزون" : "Total Stock"}
                        value={stockInfo?.totalStock || totalQtyFromVariants}
                    />
                    <StatCard
                        icon={FiCheckCircle}
                        label={isArabic ? "المتاح للبيع" : "Available"}
                        value={stockInfo?.availableStock || 0}
                        color="text-emerald-500"
                    />
                    <StatCard
                        icon={FiShoppingCart}
                        label={isArabic ? "محجوز" : "Reserved"}
                        value={stockInfo?.reservedStock || 0}
                        color="text-amber-500"
                    />
                    <StatCard
                        icon={FiTag}
                        label={isArabic ? "عدد الخصائص" : "Properties"}
                        value={product.variants?.length || 0}
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Column: Images & Variants */}
                    <div className="lg:col-span-2 space-y-6">

                        {/* Visuals Card */}
                        <Card title={isArabic ? "معرض الصور" : "Image Gallery"} icon={FiImage}>
                            <div className="flex flex-col md:flex-row gap-6">
                                {/* Main Image */}
                                <div className="flex-1 aspect-square rounded-3xl overflow-hidden bg-gray-50 border border-gray-100 dark:bg-gray-900/50 dark:border-gray-800 flex items-center justify-center p-4">
                                    <img
                                        key={activeImage}
                                        src={buildImgUrl(activeImage)}
                                        alt="Product"
                                        className="max-w-full max-h-full object-contain drop-shadow-2xl transition-opacity duration-300"
                                    />
                                </div>

                                {/* Thumbnails */}
                                {gallery.length > 0 && (
                                    <div className="flex md:flex-col gap-3 overflow-x-auto md:overflow-y-auto no-scrollbar md:w-24">
                                        {gallery.map((img, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setActiveImage(img)}
                                                className={clsx(
                                                    "w-16 h-16 md:w-20 md:h-20 shrink-0 rounded-2xl border-2 transition-all p-1 bg-white",
                                                    activeImage === img ? "border-purple-600 scale-105 shadow-md" : "border-transparent opacity-60 active:opacity-100"
                                                )}
                                            >
                                                <img src={buildImgUrl(img)} className="w-full h-full object-cover rounded-xl" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* Variants Table Card */}
                        <Card title={isArabic ? "خصائص المنتج" : "Product Properties"} icon={FiLayout}>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-center">
                                    <thead>
                                        <tr className="border-b border-gray-50 dark:border-gray-800 text-gray-400 font-bold">
                                            <th className="pb-4 px-2">{isArabic ? "الخاصية" : "Property"}</th>
                                            <th className="pb-4 px-2">{isArabic ? "اللون / النوع" : "Color / Type"}</th>
                                            <th className="pb-4 px-2">{isArabic ? "المقاس" : "Size"}</th>
                                            <th className="pb-4 px-2">{isArabic ? "الكمية" : "Qty"}</th>
                                            <th className="pb-4 px-2">{isArabic ? "نقص المخزون" : "Low Stock"}</th>
                                            <th className="pb-4 px-2">{isArabic ? "السعر" : "Price"}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                                        {product.variants?.flatMap((v, i) => {
                                            const sizes = Array.isArray(v.sizes) && v.sizes.length > 0
                                                ? v.sizes
                                                : [{
                                                    size: "—",
                                                    stockQty: v.stock?.quantity ?? v.stockQty ?? v.quantity ?? 0,
                                                    lowStockValue: v.stock?.lowStockValue ?? v.lowStockValue ?? 0,
                                                }];

                                            return sizes.map((sz, szIdx) => {
                                                const szQty = Number(sz.stockQty ?? sz.StockQty ?? 0);
                                                const lowStock = Number(
                                                    sz.lowStockValue ??
                                                    sz.LowStockValue ??
                                                    v.stock?.lowStockValue ??
                                                    v.lowStockValue ??
                                                    0
                                                );
                                                const isAtLowStock = Number.isFinite(lowStock) && lowStock > 0 && szQty > 0 && szQty <= lowStock;
                                                return (
                                                    <tr key={`${i}-${szIdx}`} className="group transition-colors">
                                                        <td className="py-4 px-2 font-bold">{v.variantName}</td>
                                                        <td className="py-4 px-2">
                                                            <div className="flex items-center justify-center gap-2">
                                                                {v.color?.hexCode && (
                                                                    <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: v.color.hexCode }} title={v.color.name} />
                                                                )}
                                                                <span className="text-xs opacity-70">{v.color?.name || "—"}</span>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-2 text-xs font-semibold opacity-80">
                                                            {sz.size || sz.Size || "—"}
                                                        </td>
                                                        <td className="py-4 px-2">
                                                            <span className={clsx(
                                                                "font-black",
                                                                szQty <= 0 ? "text-red-500" : isAtLowStock ? "text-amber-500" : "text-purple-600"
                                                            )}>
                                                                {szQty}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-2">
                                                            <span
                                                                className={clsx(
                                                                    "inline-flex items-center justify-center min-w-[2rem] px-2.5 py-1 rounded-full text-xs font-bold tabular-nums",
                                                                    isAtLowStock
                                                                        ? "bg-amber-500/10 text-amber-600 ring-1 ring-amber-500/20"
                                                                        : isDark
                                                                            ? "bg-slate-800/80 text-slate-300"
                                                                            : "bg-gray-50 text-gray-500"
                                                                )}
                                                                title={isArabic ? "حد نقص المخزون" : "Low stock threshold"}
                                                            >
                                                                {Number.isFinite(lowStock) ? lowStock : 0}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-2 font-bold text-gray-900 dark:text-white">
                                                            {Number(v.price).toLocaleString()} <span className="text-[10px] opacity-40">{isArabic ? "د.ع" : "IQD"}</span>
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Card>

                    </div>

                    {/* Sidebar Column: Info & Status */}
                    <div className="space-y-6">

                        {/* Status Card */}
                        <Card className="p-0 overflow-hidden">
                            <div className={clsx("p-6 flex flex-col items-center gap-4 text-center", stockStatus.bg)}>
                                <div className={clsx("w-16 h-16 rounded-full flex items-center justify-center border-4 border-white dark:border-gray-800 shadow-lg", stockStatus.color, "bg-white dark:bg-gray-900")}>
                                    <stockStatus.icon size={32} />
                                </div>
                                <div>
                                    <h4 className={clsx("text-lg font-black", stockStatus.color)}>{stockStatus.label}</h4>
                                    <p className="text-xs opacity-60 font-medium mt-1">
                                        {isArabic ? "حالة المخزون الحالية بناءً على الكميات المتوفرة" : "Current stock status based on available quantities"}
                                    </p>
                                </div>
                            </div>
                        </Card>

                        {/* Information Card */}
                        <Card title={isArabic ? "معلومات عامة" : "General Info"} icon={FiInfo}>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center text-sm border-b pb-3 border-gray-50 dark:border-gray-800">
                                    <span className="text-gray-400 font-bold">{isArabic ? "اسم المنتج" : "Name"}</span>
                                    <span className="font-bold text-end max-w-[150px]">{product.itemName}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm border-b pb-3 border-gray-50 dark:border-gray-800">
                                    <span className="text-gray-400 font-bold">{isArabic ? "الفئة / البراند" : "Category / Brand"}</span>
                                    <span className="font-bold">{product.category?.name || "—"}</span>
                                </div>
                                {product.discountValue > 0 && (
                                    <div className="flex justify-between items-center text-sm border-b pb-3 border-gray-50 dark:border-gray-800">
                                        <span className="text-gray-400 font-bold">{isArabic ? "الخصم" : "Discount"}</span>
                                        <span className="font-bold text-emerald-600">
                                            {product.discountValue} {product.discountType === 1 ? "%" : (isArabic ? "د.ع" : "IQD")}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center text-sm border-b pb-3 border-gray-50 dark:border-gray-800">
                                    <span className="text-gray-400 font-bold">{isArabic ? "البراند" : "Tag / Type"}</span>
                                    <span className="font-bold">{product.tags?.name || "—"}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-400 font-bold">{isArabic ? "حالة المنتج" : "Status"}</span>
                                    <span className={clsx("font-bold px-2 py-0.5 rounded-lg text-xs", product.isActive ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600")}>
                                        {product.isActive ? (isArabic ? "نشط" : "Active") : (isArabic ? "متوقف" : "Inactive")}
                                    </span>
                                </div>
                            </div>
                        </Card>

                        {/* Description Card */}
                        <Card title={isArabic ? "الوصف" : "Description"} icon={FiLayout}>
                            <div className="text-xs leading-relaxed opacity-80 text-justify">
                                {product.description || (isArabic ? "لا يوجد وصف متوفر لهذا المنتج." : "No description available for this product.")}
                            </div>
                        </Card>

                    </div>
                </div>

            </div>
        </PageContainer >
    );
}
